'use client'

import React, { useState } from 'react'
import styles from './TerminalTabs.module.css'
import Holdings from './Holdings'
import OrderHistory from './OrderHistory'
import TradeHistory from './TradeHistory'
import OpenOrders from './OpenOrders'

const TABS = ['Open Orders', 'Order History', 'Trade History', 'Holdings']

/**
 * @uiComponent
 * @name TerminalTabs
 * @description The main tabbed interface for the Binance trading terminal. Provides navigation between
 * different trading-related views including Open Orders, Order History, and Holdings.
 * Simplified version optimized for cryptocurrency trading with fewer tabs than stock trading terminals.
 * @location Bottom panel of the trading interface
 * @prop none - No external props, manages internal state for tab navigation
 */

/**
 * @uiWorkflow
 * @name Navigate Crypto Trading Information on Binance Terminal
 * @description How to access different types of cryptocurrency trading information and account data
 * @platform binance
 * @step 1 Locate the terminal tabs at the bottom of the trading interface [component: TerminalTabs] [action: observe]
 * @step 2 Click on "Open Orders" to view pending crypto buy/sell orders [component: TerminalTabs] [action: click]
 * @step 3 Switch to "Order History" to review past completed orders [component: TerminalTabs] [action: click]
 * @step 4 View "Holdings" to see your cryptocurrency portfolio [component: TerminalTabs] [action: click]
 * @step 5 Use the tab navigation to switch between different views [component: TerminalTabs] [action: click]
 * @step 6 Each tab loads relevant data specific to that crypto trading aspect [component: TerminalTabs] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Monitor Crypto Trading Activity Overview on Binance
 * @description How to get a comprehensive view of your cryptocurrency trading activity
 * @platform binance
 * @step 1 Start with the "Open Orders" tab to see pending crypto activity [component: OpenOrders] [action: click]
 * @step 2 Review any orders waiting to be executed or filled [component: OpenOrders] [action: observe]
 * @step 3 Switch to "Order History" for recently completed transactions [component: OrderHistory] [action: click]
 * @step 4 Review executed orders and trading performance [component: OrderHistory] [action: observe]
 * @step 5 Check "Holdings" for your cryptocurrency portfolio [component: Holdings] [action: click]
 * @step 6 Monitor portfolio value and asset allocation [component: Holdings] [action: observe]
 * @step 7 Use this comprehensive view to make informed crypto trading decisions [component: TerminalTabs] [action: analyze]
 */

export default function TerminalTabs() {
  const [activeTab, setActiveTab] = useState<string>(TABS[0])

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Open Orders':
        return <OpenOrders />
      case 'Order History':
        return <OrderHistory />
      case 'Trade History':
        return <TradeHistory />
      case 'Holdings':
        return <Holdings />
      default:
        return null
    }
  }

  return (
    <div className={styles.tabsContainer}>
      <ul className={styles.tabList}>
        {TABS.map(tab => (
          <li
            key={tab}
            className={`${styles.tabItem} ${
              tab === activeTab ? styles.activeTab : ''
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </li>
        ))}
      </ul>
      <div className={styles.tabContent}>{renderTabContent()}</div>
    </div>
  )
}
